import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "../AuthContext";

type AdminTab = "overview" | "families" | "push" | "seo" | "campaigns";

type MetricBag = {
  totalUsers: number;
  verifiedUsers: number;
  newUsersToday: number;
  newUsers7d: number;
  liveFamilies: number;
  activeSubscribers: number;
  monthlySubscribers: number;
  yearlySubscribers: number;
  estimatedMrrCents: number;
  totalStories: number;
  stories7d: number;
  totalPrintOrders: number;
  printOrders30d: number;
  recoverableAccounts: number;
  marketingEligible: number;
};

type Overview = {
  generatedAt: string;
  metrics: MetricBag;
  recentEvents: Array<{
    id: string;
    type: string;
    severity: string;
    message: string;
    createdAt: string;
  }>;
  stripe?: {
    configured: boolean;
    available: boolean;
    paidSubscriptionInvoices30dCents: number | null;
  };
};

type Customer = {
  id: string;
  name: string;
  email: string;
  language: string;
  country: string | null;
  emailVerified: boolean;
  marketingConsentAt: string | null;
  subscriptionStatus: string;
  subscriptionPlan: string | null;
  subscriptionCurrentPeriodEnd: string | null;
  createdAt: string;
  lastActiveAt: string | null;
  storyCount: number;
  childProfileCount: number;
  deletedAt: string | null;
  restoreUntil: string | null;
};

type Customers = { active: Customer[]; deleted: Customer[] };
type PushStatus = {
  configured: boolean;
  publicKey: string | null;
  subscribers: number;
};
type SeoDraft = {
  id: string;
  title: string;
  keyword: string;
  language: string;
  summary: string;
  status: "draft" | "review" | "published" | "archived";
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
  page?: { path: string; heading: string; description: string } | null;
};
type SeoDashboard = {
  publishedCount: number;
  policy: { automaticPublishing: boolean; note: string };
  suggestions: Array<{ keyword: string; language: string }>;
  drafts: SeoDraft[];
  integrations: { searchConsoleConfigured: boolean; analyticsConfigured: boolean; sitemapConfigured: boolean };
  searchPerformance: {
    connected: boolean;
    updatedAt: string | null;
    rows: Array<{ query: string; page: string; position: number; googlePage: number; clicks: number; impressions: number; ctr: number }>;
  };
};
type Campaign = {
  id: string;
  topic: string;
  language: string;
  channel: "email" | "instagram" | "tiktok";
  title: string;
  message: string;
  status: "draft" | "ready" | "sent" | "archived";
  createdAt: string;
  recipientCount?: number;
  failedCount?: number;
  sentAt?: string;
  contentPilot?: { hook: string; cta: string; video: { assetUrl: string; targetDurationSeconds: number; storyboard: Array<{ order: number; durationSeconds: number; caption: string }> } } | null;
};
type ContentPilotRun = {
  id: string;
  createdAt: string;
  generatedBy: string;
  plan: { topic: string; hook: string; cta: string; language: string; researchNote: string; video: { assetUrl: string; targetDurationSeconds: number; renderStatus: string; renderSource: string; subtitles: string[]; storyboard: Array<{ order: number; durationSeconds: number; caption: string }> } };
};
type CampaignDashboard = {
  campaigns: Campaign[];
  eligibleRecipients: number;
  connections: Record<
    "email" | "instagram" | "tiktok",
    { configured: boolean; connected: boolean }
  >;
  note: string;
  ai: { configured: boolean; provider: string };
  audience: Array<{ id: string; name: string; email: string; language: string; verified: boolean; allowed: boolean; storyCount: number; subscriptionPlan: string | null; lastActiveAt: string | null }>;
  automation: { enabled: boolean; intervalHours: number; lastRunAt: string | null; nextRunAt: string | null };
  contentPilotRuns: ContentPilotRun[];
};

const euro = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
});
const dateTime = new Intl.DateTimeFormat("de-DE", {
  dateStyle: "medium",
  timeStyle: "short",
});

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    credentials: "same-origin",
    ...options,
    headers: { "Content-Type": "application/json", ...options.headers },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok)
    throw new Error(payload.error || "Die Anfrage ist fehlgeschlagen.");
  return payload as T;
}

function formatDate(value: string | null) {
  if (!value) return "–";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "–" : dateTime.format(date);
}

function decodeVapidKey(value: string) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from(raw, (character) => character.charCodeAt(0));
}

function Metric({
  label,
  value,
  hint,
}: {
  label: string;
  value: string | number;
  hint?: string;
}) {
  return (
    <section className="rounded-[1.45rem] border border-white/10 bg-white/[0.055] p-5 shadow-[0_18px_50px_rgba(0,0,0,.14)]">
      <p className="text-xs font-black uppercase tracking-[.16em] text-[#f7e8b0]/70">
        {label}
      </p>
      <p className="mt-2 text-3xl font-black text-white">{value}</p>
      {hint ? <p className="mt-1 text-sm text-white/55">{hint}</p> : null}
    </section>
  );
}

function FamilyCard({ customer }: { customer: Customer }) {
  const plan =
    customer.subscriptionStatus === "active"
      ? customer.subscriptionPlan === "yearly"
        ? "Jahresabo"
        : "Monatsabo"
      : "Kein Abo";

  return (
    <article className="rounded-3xl border border-white/10 bg-[#171939]/75 p-4 shadow-[0_18px_50px_rgba(0,0,0,.12)]">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-lg font-black text-white">
            {customer.name || "Ohne Namen"}
          </h3>
          <p className="mt-0.5 break-all text-sm text-white/60">
            {customer.email}
          </p>
          <p className="mt-1 text-xs text-white/40">
            {customer.emailVerified ? "E-Mail bestätigt" : "E-Mail offen"}
            {customer.marketingConsentAt ? " · Kontakt erlaubt" : ""}
          </p>
        </div>
        <span className="shrink-0 rounded-full border border-emerald-300/30 bg-emerald-300/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-[.12em] text-emerald-100">
          {plan}
        </span>
      </div>
      <dl className="mt-4 grid grid-cols-2 gap-x-3 gap-y-3 border-t border-white/10 pt-3 text-sm">
        <div>
          <dt className="text-[10px] font-black uppercase tracking-[.13em] text-white/40">
            Land
          </dt>
          <dd className="mt-1 font-bold text-white/85">
            {customer.country || "–"}
          </dd>
        </div>
        <div>
          <dt className="text-[10px] font-black uppercase tracking-[.13em] text-white/40">
            Geschichten
          </dt>
          <dd className="mt-1 font-bold text-white/85">
            {customer.storyCount}
          </dd>
        </div>
        <div className="col-span-2">
          <dt className="text-[10px] font-black uppercase tracking-[.13em] text-white/40">
            Zuletzt aktiv
          </dt>
          <dd className="mt-1 font-bold text-white/85">
            {formatDate(customer.lastActiveAt)}
          </dd>
        </div>
      </dl>
    </article>
  );
}

export default function AdminDashboard() {
  const { user, loading: authLoading, login, logout } = useAuth();
  const [tab, setTab] = useState<AdminTab>("overview");
  const [overview, setOverview] = useState<Overview | null>(null);
  const [customers, setCustomers] = useState<Customers | null>(null);
  const [pushStatus, setPushStatus] = useState<PushStatus | null>(null);
  const [seo, setSeo] = useState<SeoDashboard | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [seoWorking, setSeoWorking] = useState(false);
  const [seoKeyword, setSeoKeyword] = useState("");
  const [seoLanguage, setSeoLanguage] = useState("de");
  const [seoTitle, setSeoTitle] = useState("");
  const [seoCount, setSeoCount] = useState(3);
  const [seoPublishDirectly, setSeoPublishDirectly] = useState(false);
  const [campaignWorking, setCampaignWorking] = useState(false);
  const [campaignTopic, setCampaignTopic] = useState("");
  const [campaignLanguage, setCampaignLanguage] = useState("de");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  const load = useCallback(async () => {
    if (!user?.ownerAccess) return;
    setLoading(true);
    setError("");
    try {
      const [
        nextOverview,
        nextCustomers,
        nextPushStatus,
        nextSeo,
        nextCampaigns,
      ] = await Promise.all([
        request<Overview>("/api/admin/overview"),
        request<Customers>("/api/admin/customers"),
        request<PushStatus>("/api/admin/push/status"),
        request<SeoDashboard>("/api/admin/seo"),
        request<CampaignDashboard>("/api/admin/campaigns"),
      ]);
      setOverview(nextOverview);
      setCustomers(nextCustomers);
      setPushStatus(nextPushStatus);
      setSeo(nextSeo);
      setCampaigns(nextCampaigns);
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : "Das Dashboard konnte nicht geladen werden.",
      );
    } finally {
      setLoading(false);
    }
  }, [user?.ownerAccess]);

  useEffect(() => {
    void load();
  }, [load]);

  const activatePush = async () => {
    setMessage("");
    setError("");
    try {
      if (!pushStatus?.configured || !pushStatus.publicKey) {
        throw new Error(
          "Push ist auf dem Server noch nicht eingerichtet. Führe zuerst einmal „npm run push:setup“ aus und übertrage die App live.",
        );
      }
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        throw new Error(
          "Dieses Gerät unterstützt Browser-Push nicht. Auf dem iPhone: Lumi zuerst über „Zum Home-Bildschirm“ installieren und dann dort öffnen.",
        );
      }
      const permission = await Notification.requestPermission();
      if (permission !== "granted")
        throw new Error(
          "Benachrichtigungen wurden nicht erlaubt. Bitte aktiviere sie in den Einstellungen dieses Geräts.",
        );
      const registration = await navigator.serviceWorker.ready;
      const subscription =
        (await registration.pushManager.getSubscription()) ||
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: decodeVapidKey(pushStatus.publicKey),
        }));
      await request("/api/admin/push/subscriptions", {
        method: "POST",
        body: JSON.stringify({ subscription: subscription.toJSON() }),
      });
      setMessage("Dieses Gerät ist verbunden. Schicke jetzt eine Testmeldung.");
      await load();
    } catch (pushError) {
      setError(
        pushError instanceof Error
          ? pushError.message
          : "Push konnte nicht aktiviert werden.",
      );
    }
  };

  const sendTestPush = async () => {
    setMessage("");
    setError("");
    try {
      const result = await request<{ delivered: number }>(
        "/api/admin/push/test",
        { method: "POST", body: "{}" },
      );
      setMessage(
        `Test gesendet – ${result.delivered} verbundenes Gerät${result.delivered === 1 ? "" : "e"} benachrichtigt.`,
      );
    } catch (pushError) {
      setError(
        pushError instanceof Error
          ? pushError.message
          : "Der Test konnte nicht gesendet werden.",
      );
    }
  };

  const restoreAccount = async (customer: Customer) => {
    if (
      !window.confirm(
        `Konto von ${customer.email} mit Geschichten und Profilen wiederherstellen?`,
      )
    )
      return;
    setError("");
    try {
      await request(
        `/api/admin/customers/${encodeURIComponent(customer.id)}/restore`,
        { method: "POST", body: "{}" },
      );
      setMessage(`${customer.name || customer.email} wurde wiederhergestellt.`);
      await load();
    } catch (restoreError) {
      setError(
        restoreError instanceof Error
          ? restoreError.message
          : "Das Konto konnte nicht wiederhergestellt werden.",
      );
    }
  };

  const generateSeoPage = async (event: FormEvent) => {
    event.preventDefault();
    setMessage("");
    setError("");
    setSeoWorking(true);
    try {
      const result = await request<{ draft: SeoDraft }>(
        "/api/admin/seo/generate",
        {
          method: "POST",
          body: JSON.stringify({
            keyword: seoKeyword,
            language: seoLanguage,
            title: seoTitle,
          }),
        },
      );
      setSeoKeyword("");
      setSeoTitle("");
      setMessage(
        `Der Seitenentwurf „${result.draft.title}“ ist bereit. Prüfe ihn unten und veröffentliche ihn erst dann.`,
      );
      await load();
    } catch (seoError) {
      setError(
        seoError instanceof Error
          ? seoError.message
          : "Der Seitenentwurf konnte nicht erstellt werden.",
      );
    } finally {
      setSeoWorking(false);
    }
  };

  const generateSeoBatch = async (event: FormEvent) => {
    event.preventDefault();
    if (
      seoPublishDirectly &&
      !window.confirm(`${seoCount} neue Seiten sofort öffentlich erstellen?`)
    )
      return;
    setMessage("");
    setError("");
    setSeoWorking(true);
    try {
      const result = await request<{ drafts: SeoDraft[]; languages: string[] }>(
        "/api/admin/seo/autopilot",
        {
          method: "POST",
          body: JSON.stringify({
            count: seoCount,
            publish: seoPublishDirectly,
          }),
        },
      );
      setMessage(
        `${result.drafts.length} marktbasierte SEO-Seiten wurden automatisch für ${result.languages.length} Sprachen ${seoPublishDirectly ? "veröffentlicht" : "vorbereitet"}.`,
      );
      await load();
    } catch (seoError) {
      setError(
        seoError instanceof Error
          ? seoError.message
          : "Die SEO-Maschine konnte nicht gestartet werden.",
      );
    } finally {
      setSeoWorking(false);
    }
  };

  const publishSeoPage = async (draft: SeoDraft) => {
    if (
      !window.confirm(
        `„${draft.title}“ jetzt öffentlich auf lumi-traumwelt.com veröffentlichen? Die Seite erscheint direkt in der Sitemap.`,
      )
    )
      return;
    setMessage("");
    setError("");
    setSeoWorking(true);
    try {
      const result = await request<{ draft: SeoDraft }>(
        `/api/admin/seo/drafts/${encodeURIComponent(draft.id)}/publish`,
        { method: "POST", body: "{}" },
      );
      setMessage(
        `Live: ${result.draft.page?.path || "die neue Seite"} ist veröffentlicht und wurde zur Sitemap hinzugefügt.`,
      );
      await load();
    } catch (seoError) {
      setError(
        seoError instanceof Error
          ? seoError.message
          : "Die Seite konnte nicht veröffentlicht werden.",
      );
    } finally {
      setSeoWorking(false);
    }
  };

  const archiveSeoPage = async (draft: SeoDraft) => {
    if (
      !window.confirm(
        `„${draft.title}“ aus dem öffentlichen Bereich entfernen?`,
      )
    )
      return;
    setMessage("");
    setError("");
    setSeoWorking(true);
    try {
      await request(`/api/admin/seo/drafts/${encodeURIComponent(draft.id)}`, {
        method: "PATCH",
        body: JSON.stringify({ status: "archived" }),
      });
      setMessage("Die Seite wurde aus dem öffentlichen Bereich entfernt.");
      await load();
    } catch (seoError) {
      setError(
        seoError instanceof Error
          ? seoError.message
          : "Die Seite konnte nicht entfernt werden.",
      );
    } finally {
      setSeoWorking(false);
    }
  };

  const generateCampaign = async (event: FormEvent) => {
    event.preventDefault();
    setMessage("");
    setError("");
    setCampaignWorking(true);
    try {
      await request("/api/admin/campaigns/generate", {
        method: "POST",
        body: JSON.stringify({
          topic: campaignTopic,
          language: campaignLanguage,
          allLanguages: true,
        }),
      });
      setCampaignTopic("");
      setMessage(
        "Die Kampagne ist für alle angebotenen Sprachen und drei Kanäle vorbereitet.",
      );
      await load();
    } catch (campaignError) {
      setError(
        campaignError instanceof Error
          ? campaignError.message
          : "Die Kampagne konnte nicht vorbereitet werden.",
      );
    } finally {
      setCampaignWorking(false);
    }
  };

  const copyCampaign = async (campaign: Campaign) => {
    setMessage("");
    setError("");
    try {
      await navigator.clipboard.writeText(
        `${campaign.title}\n\n${campaign.message}`,
      );
      setMessage(
        `${campaign.channel === "email" ? "E-Mail" : campaign.channel === "instagram" ? "Instagram" : "TikTok"}-Text wurde kopiert.`,
      );
    } catch {
      setError(
        "Der Text konnte nicht kopiert werden. Bitte markiere ihn direkt im Entwurf.",
      );
    }
  };

  const sendCampaign = async (campaign: Campaign) => {
    const count = campaigns?.eligibleRecipients || 0;
    if (!window.confirm(`Diese E-Mail jetzt einzeln an ${count} erlaubte Kontakte senden?`)) return;
    setMessage("");
    setError("");
    setCampaignWorking(true);
    try {
      const result = await request<{ delivered: number; failed: number }>(
        `/api/admin/campaigns/${encodeURIComponent(campaign.id)}/send`,
        { method: "POST", body: "{}" },
      );
      setMessage(`${result.delivered} E-Mails wurden versendet${result.failed ? ` · ${result.failed} fehlgeschlagen` : ""}.`);
      await load();
    } catch (sendError) {
      setError(sendError instanceof Error ? sendError.message : "Die E-Mail-Kampagne konnte nicht versendet werden.");
    } finally {
      setCampaignWorking(false);
    }
  };

  const publishSocialCampaign = async (campaign: Campaign) => {
    const platform = campaign.channel === "instagram" ? "Instagram" : "TikTok";
    if (!window.confirm(`Diesen Beitrag mit dem fertigen Lumi-Video jetzt öffentlich auf ${platform} posten?`)) return;
    setMessage(""); setError(""); setCampaignWorking(true);
    try {
      await request(`/api/admin/campaigns/${encodeURIComponent(campaign.id)}/publish`, { method: "POST", body: "{}" });
      setMessage(`Der Beitrag wurde an ${platform} übergeben.`);
      await load();
    } catch (publishError) {
      setError(publishError instanceof Error ? publishError.message : `Der Beitrag konnte nicht auf ${platform} veröffentlicht werden.`);
    } finally { setCampaignWorking(false); }
  };

  const toggleSocialAutomation = async () => {
    const enabled = !campaigns?.automation?.enabled;
    if (enabled && !window.confirm("Social-Autopilot einschalten? Lumi erstellt und veröffentlicht dann alle 24 Stunden neue Beiträge auf beiden verbundenen Kanälen.")) return;
    setCampaignWorking(true); setError(""); setMessage("");
    try {
      await request("/api/admin/campaigns/automation", { method: "POST", body: JSON.stringify({ enabled }) });
      setMessage(enabled ? "Social-Autopilot ist eingeschaltet." : "Social-Autopilot ist pausiert.");
      await load();
    } catch (automationError) { setError(automationError instanceof Error ? automationError.message : "Die Automatik konnte nicht geändert werden."); }
    finally { setCampaignWorking(false); }
  };

  const runContentPilot = async () => {
    setCampaignWorking(true); setError(""); setMessage("");
    try {
      const result = await request<{ run: ContentPilotRun; campaigns: Campaign[] }>("/api/admin/content-pilot/run", { method: "POST", body: JSON.stringify({ language: "de" }) });
      setMessage(`Neuer Content-Plan bereit: ${result.run.plan.topic}. Zwei Posts und ein Kurzvideo-Entwurf wurden erstellt.`);
      await load();
    } catch (pilotError) { setError(pilotError instanceof Error ? pilotError.message : "Der Content-Pilot konnte nicht gestartet werden."); }
    finally { setCampaignWorking(false); }
  };

  const submitLogin = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    try {
      await login(loginEmail, loginPassword);
    } catch (loginError) {
      setError(
        loginError instanceof Error
          ? loginError.message
          : "Die Anmeldung ist fehlgeschlagen.",
      );
    }
  };

  const activeCustomers = useMemo(() => customers?.active ?? [], [customers]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#07071d] text-white grid place-items-center">
        Lumi Verwaltung wird geöffnet …
      </div>
    );
  }

  if (!user) {
    return (
      <main className="admin-scroll-container min-h-screen bg-[radial-gradient(circle_at_20%_0%,#30366d_0%,#0b0b27_46%,#040513_100%)] px-5 py-12 text-white">
        <form
          onSubmit={submitLogin}
          className="mx-auto mt-16 w-full max-w-md rounded-[2rem] border border-white/15 bg-white/[0.07] p-8 shadow-2xl backdrop-blur-xl"
        >
          <p className="text-sm font-black uppercase tracking-[.2em] text-[#f8e9ae]">
            Lumi intern
          </p>
          <h1 className="mt-2 text-4xl font-black">Verwaltung</h1>
          <p className="mt-3 text-white/65">
            Nur für das Lumi-Team. Dieser Bereich erscheint nicht in der
            Familien-App.
          </p>
          <label className="mt-7 block text-sm font-bold">
            E-Mail
            <input
              value={loginEmail}
              onChange={(event) => setLoginEmail(event.target.value)}
              type="email"
              autoComplete="email"
              required
              className="mt-2 w-full rounded-2xl border border-white/15 bg-[#090a25] px-4 py-3 text-white outline-none"
            />
          </label>
          <label className="mt-4 block text-sm font-bold">
            Passwort
            <input
              value={loginPassword}
              onChange={(event) => setLoginPassword(event.target.value)}
              type="password"
              autoComplete="current-password"
              required
              className="mt-2 w-full rounded-2xl border border-white/15 bg-[#090a25] px-4 py-3 text-white outline-none"
            />
          </label>
          {error ? (
            <p className="mt-4 rounded-xl bg-red-500/15 p-3 text-sm text-red-100">
              {error}
            </p>
          ) : null}
          <button
            className="mt-6 w-full rounded-2xl bg-[#f8e9ae] px-5 py-4 font-black text-[#10112e]"
            type="submit"
          >
            Sicher anmelden
          </button>
        </form>
      </main>
    );
  }

  if (!user.ownerAccess) {
    return (
      <main className="admin-scroll-container grid min-h-screen place-items-center bg-[#07071d] px-5 text-center text-white">
        <div>
          <h1 className="text-3xl font-black">Kein Zugriff</h1>
          <p className="mt-3 text-white/65">
            Dieser Bereich ist nur für die Lumi-Verwaltung.
          </p>
          <button
            onClick={() => void logout()}
            className="mt-6 rounded-xl border border-white/20 px-4 py-2"
          >
            Abmelden
          </button>
        </div>
      </main>
    );
  }

  const metrics = overview?.metrics;
  return (
    <main className="admin-scroll-container min-h-screen bg-[radial-gradient(circle_at_10%_0%,#343b73_0%,#111335_42%,#060718_100%)] px-4 py-5 text-white sm:px-8 sm:py-8">
      <div className="mx-auto max-w-7xl pb-12">
        <header className="flex flex-col gap-5 rounded-[2rem] border border-white/10 bg-white/[0.065] p-6 shadow-2xl backdrop-blur-xl sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[.18em] text-[#f8e9ae]">
              Privater Betreiberbereich
            </p>
            <h1 className="mt-1 text-3xl font-black sm:text-4xl">
              Lumi Verwaltung
            </h1>
            <p className="mt-2 text-sm text-white/60">
              Familien, Abos, Buchbestellungen und Betreiber-Meldungen.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => void load()}
              className="rounded-xl border border-white/20 px-4 py-2 text-sm font-bold"
            >
              Aktualisieren
            </button>
            <button
              onClick={() => void logout()}
              className="rounded-xl bg-white/10 px-4 py-2 text-sm font-bold"
            >
              Abmelden
            </button>
          </div>
        </header>

        <nav
          className="mt-5 flex gap-2 overflow-x-auto pb-1"
          aria-label="Verwaltung"
        >
          {(
            [
              ["overview", "Übersicht"],
              ["families", "Familien"],
              ["push", "Handy-Push"],
              ["seo", "Google-Unterseiten"],
              ["campaigns", "Kampagnen"],
            ] as Array<[AdminTab, string]>
          ).map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`shrink-0 rounded-xl px-4 py-2.5 text-sm font-black ${tab === id ? "bg-[#f8e9ae] text-[#0b0c27]" : "border border-white/15 bg-white/[.06] text-white/75"}`}
            >
              {label}
            </button>
          ))}
        </nav>

        {message ? (
          <p className="mt-5 rounded-2xl border border-emerald-300/35 bg-emerald-300/10 px-5 py-4 font-semibold text-emerald-50">
            {message}
          </p>
        ) : null}
        {error ? (
          <p className="mt-5 rounded-2xl border border-red-300/35 bg-red-500/15 px-5 py-4 font-semibold text-red-100">
            {error}
          </p>
        ) : null}
        {loading ? (
          <p className="mt-8 text-white/70">Daten werden geladen …</p>
        ) : null}

        {!loading && tab === "overview" && metrics ? (
          <section className="mt-6">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Metric
                label="Familien"
                value={metrics.totalUsers}
                hint={`${metrics.newUsersToday} neu heute`}
              />
              <Metric
                label="Gerade online"
                value={metrics.liveFamilies}
                hint="aktiv in den letzten 5 Minuten"
              />
              <Metric
                label="Aktive Abos"
                value={metrics.activeSubscribers}
                hint={`${metrics.monthlySubscribers} monatlich · ${metrics.yearlySubscribers} jährlich`}
              />
              <Metric
                label="Abo-Umsatz/Monat"
                value={euro.format(metrics.estimatedMrrCents / 100)}
                hint="Planungswert, keine Auszahlung"
              />
              <Metric
                label="Geschichten"
                value={metrics.totalStories}
                hint={`${metrics.stories7d} in den letzten 7 Tagen`}
              />
              <Metric
                label="Buchbestellungen"
                value={metrics.totalPrintOrders}
                hint={`${metrics.printOrders30d} in den letzten 30 Tagen`}
              />
              <Metric
                label="Wiederherstellbar"
                value={metrics.recoverableAccounts}
                hint="gelöscht, maximal 30 Tage"
              />
              <Metric
                label="Kontakt-Erlaubnis"
                value={metrics.marketingEligible}
                hint="verifizierte Einwilligungen"
              />
            </div>
            <section className="mt-5 rounded-[1.6rem] border border-white/10 bg-white/[.05] p-5">
              <h2 className="text-xl font-black">Letzte Systemereignisse</h2>
              <div className="mt-4 space-y-3">
                {overview.recentEvents.length ? (
                  overview.recentEvents.map((event) => (
                    <div
                      className="flex flex-col justify-between gap-1 rounded-xl bg-black/15 px-4 py-3 sm:flex-row"
                      key={event.id}
                    >
                      <span>{event.message}</span>
                      <span className="text-xs text-white/50">
                        {formatDate(event.createdAt)}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-white/60">Noch keine Ereignisse.</p>
                )}
              </div>
            </section>
          </section>
        ) : null}

        {!loading && tab === "families" ? (
          <section className="mt-6 space-y-5">
            <section className="rounded-[1.6rem] border border-white/10 bg-white/[.05] p-5">
              <h2 className="text-xl font-black">
                Aktive Familien ({activeCustomers.length})
              </h2>
              <p className="mt-1 text-sm text-white/60">
                Kontaktinformationen sind nur hier für Support, Abrechnung und
                zulässige Kommunikation sichtbar.
              </p>
              <div className="mt-5 space-y-3 md:hidden">
                {activeCustomers.map((customer) => (
                  <div key={customer.id}>
                    <FamilyCard customer={customer} />
                  </div>
                ))}
              </div>
              <div className="mt-5 hidden overflow-x-auto md:block">
                <table className="min-w-[780px] w-full text-left text-sm">
                  <thead className="border-b border-white/10 text-xs uppercase tracking-wider text-[#f8e9ae]/75">
                    <tr>
                      <th className="p-3">Familie</th>
                      <th className="p-3">Land</th>
                      <th className="p-3">Abo</th>
                      <th className="p-3">Geschichten</th>
                      <th className="p-3">Zuletzt aktiv</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeCustomers.map((customer) => (
                      <tr className="border-b border-white/5" key={customer.id}>
                        <td className="p-3">
                          <p className="font-bold">{customer.name}</p>
                          <p className="text-white/55">{customer.email}</p>
                          <p className="mt-1 text-xs text-white/40">
                            {customer.emailVerified
                              ? "E-Mail bestätigt"
                              : "E-Mail offen"}
                            {customer.marketingConsentAt
                              ? " · Kontakt erlaubt"
                              : ""}
                          </p>
                        </td>
                        <td className="p-3">{customer.country || "–"}</td>
                        <td className="p-3">
                          {customer.subscriptionStatus === "active"
                            ? customer.subscriptionPlan === "yearly"
                              ? "Jahresabo"
                              : "Monatsabo"
                            : "Kein Abo"}
                        </td>
                        <td className="p-3">{customer.storyCount}</td>
                        <td className="p-3 text-white/65">
                          {formatDate(customer.lastActiveAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
            <section className="rounded-[1.6rem] border border-amber-200/20 bg-amber-100/[.045] p-5">
              <h2 className="text-xl font-black">
                Versehentlich gelöschte Konten
              </h2>
              <p className="mt-1 text-sm text-white/60">
                Die Inhalte können innerhalb von 30 Tagen wiederhergestellt
                werden. Danach werden sie endgültig entfernt.
              </p>
              <div className="mt-4 space-y-3">
                {customers?.deleted.length ? (
                  customers.deleted.map((customer) => (
                    <div
                      key={customer.id}
                      className="flex flex-col gap-3 rounded-xl bg-black/15 p-4 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div>
                        <p className="font-bold">
                          {customer.name} · {customer.email}
                        </p>
                        <p className="text-sm text-white/55">
                          {customer.storyCount} Geschichten · Wiederherstellbar
                          bis {formatDate(customer.restoreUntil)}
                        </p>
                      </div>
                      <button
                        onClick={() => void restoreAccount(customer)}
                        className="rounded-xl bg-[#f8e9ae] px-4 py-2 font-black text-[#0d0e29]"
                      >
                        Wiederherstellen
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="text-white/55">
                    Keine wiederherstellbaren Konten.
                  </p>
                )}
              </div>
            </section>
          </section>
        ) : null}

        {!loading && tab === "push" ? (
          <section className="mt-6 max-w-3xl rounded-[1.8rem] border border-white/10 bg-white/[.06] p-5 sm:p-6">
            <p className="text-xs font-black uppercase tracking-[.18em] text-[#f8e9ae]">
              Nur dein Betreiber-Handy
            </p>
            <h2 className="mt-2 text-3xl font-black">
              Push-Benachrichtigungen
            </h2>
            <p className="mt-3 max-w-2xl leading-7 text-white/70">
              Du bekommst neutrale Meldungen bei einer neuen Registrierung,
              einem aktivierten Abo und einem bezahlten Buch. Auf dem
              Sperrbildschirm stehen nie Kindername, Foto oder E-Mail.
            </p>
            <div className="mt-6 grid gap-3 sm:grid-cols-3">
              <Metric
                label="Server"
                value={pushStatus?.configured ? "Bereit" : "Einrichtung nötig"}
              />
              <Metric
                label="Verbundene Geräte"
                value={pushStatus?.subscribers ?? 0}
              />
              <Metric
                label="Status"
                value={pushStatus?.subscribers ? "Aktiv" : "Noch aus"}
              />
            </div>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <button
                onClick={() => void activatePush()}
                className="w-full rounded-2xl bg-[#f8e9ae] px-5 py-3 font-black text-[#0d0e29] sm:w-auto"
              >
                Auf diesem Handy aktivieren
              </button>
              <button
                disabled={!pushStatus?.subscribers}
                onClick={() => void sendTestPush()}
                className="w-full rounded-2xl border border-white/20 px-5 py-3 font-black disabled:cursor-not-allowed disabled:opacity-45 sm:w-auto"
              >
                Test-Push senden
              </button>
            </div>
            <div className="mt-6 rounded-2xl border border-white/10 bg-black/15 p-4 text-sm leading-6 text-white/65">
              <strong className="text-white">iPhone:</strong> Öffne exakt{" "}
              <strong className="text-white">lumi-traumwelt.com/admin</strong>{" "}
              in Safari, tippe auf Teilen → „Zum Home-Bildschirm“ und nenne das
              Icon „Lumi Admin“. Öffne anschließend genau diese installierte
              Admin-App. Danach hier auf „Auf diesem Handy aktivieren“ tippen
              und erlauben.
            </div>
          </section>
        ) : null}

        {!loading && tab === "seo" ? (
          <section className="mt-6 max-w-5xl space-y-5">
            <section className="rounded-[1.8rem] border border-white/10 bg-white/[.05] p-5 sm:p-6">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <p className="text-xs font-black uppercase tracking-[.18em] text-[#f8e9ae]">Google-Kontrollcenter</p>
                  <h2 className="mt-2 text-2xl font-black">Positionen und Google-Seiten</h2>
                  <p className="mt-2 text-sm text-white/60">
                    Echte Daten der letzten 28 Tage – keine geschätzten oder erfundenen Platzierungen.
                  </p>
                </div>
                <span className={`rounded-full px-3 py-2 text-xs font-black ${seo?.searchPerformance?.connected ? "bg-emerald-300/15 text-emerald-100" : "bg-amber-200/10 text-amber-100"}`}>
                  {seo?.searchPerformance?.connected ? `Verbunden · ${seo.searchPerformance.rows.length} Suchbegriffe` : "Google Search Console verbinden"}
                </span>
              </div>
              {seo?.searchPerformance?.rows?.length ? (
                <div className="mt-5 overflow-x-auto">
                  <table className="min-w-[720px] w-full text-left text-sm" aria-label="Google Positionen der Lumi-Seiten">
                    <thead className="border-b border-white/10 text-xs uppercase tracking-wider text-[#f8e9ae]/75">
                      <tr><th className="p-3">Suchbegriff</th><th className="p-3">Position</th><th className="p-3">Google-Seite</th><th className="p-3">Aufrufe</th><th className="p-3">Klicks</th></tr>
                    </thead>
                    <tbody>{seo.searchPerformance.rows.slice(0, 30).map((row) => (
                      <tr key={`${row.query}-${row.page}`} className="border-b border-white/5">
                        <td className="p-3"><p className="font-bold">{row.query}</p><p className="max-w-md truncate text-xs text-white/40">{row.page}</p></td>
                        <td className="p-3 font-black">{row.position.toFixed(1)}</td>
                        <td className="p-3"><span className="rounded-full bg-white/10 px-2.5 py-1 font-black">Seite {row.googlePage}</span></td>
                        <td className="p-3">{row.impressions}</td><td className="p-3">{row.clicks}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              ) : (
                <div className="mt-5 rounded-2xl border border-white/10 bg-black/15 p-4 text-sm leading-6 text-white/65">
                  Nach der Verbindung mit Google Search Console erscheinen hier automatisch Suchbegriff, genaue Position, Google-Seite, Aufrufe und Klicks. Bis dahin zeigt Lumi bewusst keine erfundenen Rankings.
                </div>
              )}
            </section>
            <section className="rounded-[1.8rem] border border-[#f8e9ae]/25 bg-[linear-gradient(135deg,rgba(248,233,174,.14),rgba(255,255,255,.04))] p-5 sm:p-7">
              <p className="text-xs font-black uppercase tracking-[.18em] text-[#f8e9ae]">
                SEO-Maschine
              </p>
              <h2 className="mt-2 text-3xl font-black">
                Nur Anzahl wählen – Lumi erledigt den Rest.
              </h2>
              <p className="mt-3 max-w-3xl leading-7 text-white/70">
                Lumi prüft aktuelle Google-Suchvorschläge, priorisiert echte
                Suchanfragen und erstellt daraus unterschiedliche Seiten. Doppelte
                Themen werden zurückgehalten; veröffentlichte Seiten landen automatisch in der Sitemap.
              </p>
              <div
                className="mt-5 flex flex-wrap gap-2"
                aria-label="Vorgeschlagene Suchthemen"
              >
                {seo?.suggestions?.map((suggestion) => (
                  <button
                    key={suggestion.keyword}
                    type="button"
                    onClick={() => {
                      setSeoKeyword(suggestion.keyword);
                      setSeoLanguage(suggestion.language);
                    }}
                    className="rounded-full border border-white/15 bg-white/[.06] px-3 py-2 text-left text-xs font-bold text-white/75 hover:border-[#f8e9ae]/60"
                  >
                    {suggestion.keyword}
                  </button>
                ))}
              </div>
              <form
                onSubmit={generateSeoBatch}
                className="mt-6 grid gap-3 md:grid-cols-[1fr_180px_auto]"
              >
                <div className="rounded-2xl border border-white/10 bg-black/15 px-4 py-3 text-sm leading-6 text-white/70">
                  Automatische Recherche und Veröffentlichung in Deutsch, Englisch, Französisch, Spanisch, Italienisch, Hindi, Arabisch, Japanisch und Chinesisch.
                </div>
                <select
                  aria-label="Anzahl der Seiten"
                  value={seoCount}
                  onChange={(event) => setSeoCount(Number(event.target.value))}
                  className="rounded-2xl border border-white/15 bg-[#0a0b28] px-4 py-3 font-bold text-white"
                >
                  <option value={1}>1 je Sprache</option>
                  <option value={2}>2 je Sprache</option>
                  <option value={3}>3 je Sprache</option>
                </select>
                <button
                  disabled={seoWorking}
                  className="rounded-2xl bg-[#f8e9ae] px-5 py-3 font-black text-[#0d0e29] disabled:opacity-50"
                >
                  {seoWorking ? "SEO arbeitet …" : "Jetzt erstellen"}
                </button>
                <label className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[.04] px-4 py-3 text-sm text-white/75 md:col-span-3">
                  <input
                    type="checkbox"
                    checked={seoPublishDirectly}
                    onChange={(event) =>
                      setSeoPublishDirectly(event.target.checked)
                    }
                    className="h-4 w-4 accent-amber-200"
                  />
                  Sofort veröffentlichen (sonst zuerst als Entwurf)
                </label>
                <p className="self-center text-xs leading-5 text-white/50">
                  {seo?.publishedCount || 0} Seiten sind bereits live.
                </p>
              </form>
            </section>
            <section className="rounded-[1.8rem] border border-sky-200/20 bg-[#0b1030] p-5 sm:p-7">
              <div className="flex flex-col justify-between gap-5 md:flex-row md:items-end">
                <div className="max-w-2xl"><p className="text-xs font-black uppercase tracking-[.18em] text-sky-100">Autonomer Content-Pilot</p><h2 className="mt-2 text-3xl font-black">Täglich ein echter Lumi-Ablauf</h2><p className="mt-3 leading-7 text-white/70">Lumi recherchiert selbst, wählt einen passenden Blickwinkel und erzeugt Hook, Untertitel, Storyboard und zwei Plattform-Posts. Das Video nutzt nur datenschutzsichere Produkt-Demo-Szenen der eigenen App.</p></div>
                <button type="button" disabled={campaignWorking} onClick={() => void runContentPilot()} className="shrink-0 rounded-2xl bg-sky-200 px-5 py-3 font-black text-[#0d0e29] disabled:opacity-50">{campaignWorking ? "Content entsteht …" : "Tagesplan erstellen"}</button>
              </div>
              {campaigns?.contentPilotRuns?.[0] ? (() => { const run = campaigns.contentPilotRuns[0]; return <article className="mt-6 grid gap-5 rounded-2xl border border-white/10 bg-black/15 p-4 lg:grid-cols-[190px_1fr]"><video className="aspect-[9/16] w-full max-h-[340px] rounded-xl bg-black object-cover" controls muted playsInline src={run.plan.video.assetUrl} /><div><p className="text-sm font-black text-sky-100">{run.plan.topic}</p><h3 className="mt-2 text-xl font-black">{run.plan.hook}</h3><p className="mt-2 text-sm leading-6 text-white/65">{run.plan.researchNote}</p><div className="mt-4 grid gap-2 sm:grid-cols-2">{run.plan.video.storyboard.map((scene) => <div key={scene.order} className="rounded-xl bg-white/[.06] px-3 py-2 text-sm"><span className="font-black text-sky-100">{scene.durationSeconds}s</span><span className="ml-2 text-white/75">{scene.caption}</span></div>)}</div><p className="mt-4 text-sm font-bold text-white/85">CTA: {run.plan.cta}</p><p className="mt-2 text-xs leading-5 text-white/45">{run.plan.video.renderSource} Für neue, individuell gerenderte Videos wird zusätzlich ein freigegebener Video-Renderer benötigt.</p></div></article>; })() : <p className="mt-5 rounded-2xl bg-black/15 p-4 text-sm text-white/55">Noch kein Tagesplan. Der Pilot braucht keine Vorgaben und kann sofort starten.</p>}
            </section>
            <section className="rounded-[1.8rem] border border-white/10 bg-white/[.05] p-5 sm:p-6">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h2 className="text-2xl font-black">Deine Seiten</h2>
                  <p className="mt-1 text-sm text-white/60">
                    Entwürfe können ohne Risiko geprüft werden. Bei „Sofort
                    live“ ist die Seite öffentlich und Suchmaschinen können sie
                    über die Sitemap finden.
                  </p>
                </div>
              </div>
              <div className="mt-5 space-y-3">
                {seo?.drafts?.length ? (
                  seo.drafts.map((draft) => (
                    <article
                      key={draft.id}
                      className="rounded-2xl border border-white/10 bg-black/15 p-4"
                    >
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="text-lg font-black text-white">
                              {draft.title}
                            </h3>
                            <span
                              className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-[.13em] ${draft.status === "published" ? "bg-emerald-300/15 text-emerald-100" : draft.status === "archived" ? "bg-white/10 text-white/55" : "bg-[#f8e9ae]/15 text-[#f8e9ae]"}`}
                            >
                              {draft.status === "published"
                                ? "Live"
                                : draft.status === "archived"
                                  ? "Entfernt"
                                  : "Entwurf"}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-white/60">
                            Suchthema: {draft.keyword} ·{" "}
                            {draft.language.toUpperCase()} ·{" "}
                            {formatDate(draft.updatedAt)}
                          </p>
                          <p className="mt-2 max-w-3xl text-sm leading-6 text-white/75">
                            {draft.summary}
                          </p>
                          {draft.page?.path ? (
                            <p className="mt-2 break-all text-xs font-bold text-[#f8e9ae]">
                              {draft.status === "published"
                                ? `Live-Adresse: lumi-traumwelt.com${draft.page.path}`
                                : `Vorgesehene Adresse: lumi-traumwelt.com${draft.page.path}`}
                            </p>
                          ) : null}
                        </div>
                        <div className="flex shrink-0 flex-wrap gap-2">
                          <>
                            {draft.status === "published" &&
                            draft.page?.path ? (
                              <a
                                href={draft.page.path}
                                target="_blank"
                                rel="noreferrer"
                                className="rounded-xl border border-white/20 px-3 py-2 text-sm font-black"
                              >
                                Vorschau
                              </a>
                            ) : null}
                          </>
                          {draft.status !== "published" &&
                          draft.status !== "archived" ? (
                            <button
                              disabled={seoWorking}
                              onClick={() => void publishSeoPage(draft)}
                              className="rounded-xl bg-[#f8e9ae] px-3 py-2 text-sm font-black text-[#0d0e29] disabled:opacity-50"
                            >
                              Sofort live
                            </button>
                          ) : null}
                          {draft.status === "published" ? (
                            <button
                              disabled={seoWorking}
                              onClick={() => void archiveSeoPage(draft)}
                              className="rounded-xl border border-red-200/25 px-3 py-2 text-sm font-black text-red-100 disabled:opacity-50"
                            >
                              Entfernen
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </article>
                  ))
                ) : (
                  <p className="rounded-2xl bg-black/15 p-5 text-white/55">
                    Noch keine Unterseite. Starte oben mit einem echten Thema,
                    nach dem Familien tatsächlich suchen.
                  </p>
                )}
              </div>
            </section>
          </section>
        ) : null}

        {!loading && tab === "campaigns" ? (
          <section className="mt-6 max-w-5xl space-y-5">
            <section className="rounded-[1.8rem] border border-[#f8e9ae]/25 bg-[linear-gradient(135deg,rgba(248,233,174,.14),rgba(255,255,255,.04))] p-5 sm:p-7">
              <p className="text-xs font-black uppercase tracking-[.18em] text-[#f8e9ae]">Ein Social-Media-Knopf</p>
              <div className="mt-2 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between"><div><h2 className="text-3xl font-black">24-Stunden-Autopilot</h2><p className="mt-2 max-w-2xl leading-7 text-white/70">Recherchiert täglich ein neues Thema, erstellt individuelle Texte und veröffentlicht auf beiden verbundenen Kanälen.</p></div><button type="button" disabled={campaignWorking} onClick={() => void toggleSocialAutomation()} className={`min-h-12 rounded-2xl px-6 py-3 font-black ${campaigns?.automation?.enabled ? "border border-emerald-200/30 bg-emerald-300/15 text-emerald-50" : "bg-[#f8e9ae] text-[#0d0e29]"}`}>{campaigns?.automation?.enabled ? "Autopilot läuft · Pausieren" : "Autopilot einschalten"}</button></div>
            </section>
            <section className="rounded-[1.8rem] border border-white/10 bg-white/[.05] p-5 sm:p-6">
              <h2 className="text-2xl font-black">Kanäle verbinden</h2>
              <p className="mt-2 text-sm leading-6 text-white/65">
                Sichere Plattformfreigabe statt Passwortspeicherung. Sobald die
                Entwickler-App freigegeben ist, wird die Anmeldung hier aktiv.
              </p>
              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                {(["email", "instagram", "tiktok"] as const).map((channel) => {
                  const connection = campaigns?.connections[channel];
                  const label = channel === "email" ? "E-Mail" : channel === "instagram" ? "Instagram" : "TikTok";
                  return (
                    <article key={channel} className="rounded-2xl border border-white/10 bg-black/15 p-4">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-black">{label}</h3>
                        <span className={`rounded-full px-2 py-1 text-[10px] font-black ${connection?.connected ? "bg-emerald-300/15 text-emerald-100" : "bg-white/10 text-white/55"}`}>
                          {connection?.connected ? "Verbunden" : connection?.configured ? "Anmeldung bereit" : "Freigabe fehlt"}
                        </span>
                      </div>
                      <p className="mt-3 text-xs leading-5 text-white/55">
                        {channel === "email" ? "Newsletter an Kontakte mit Einwilligung." : channel === "instagram" ? "Automatische Beiträge für ein professionelles Konto." : "Direkte Videos und Posts nach TikTok-Audit."}
                      </p>
                      <button type="button" disabled={!connection?.configured || connection?.connected} onClick={() => { if (channel === "tiktok" && connection?.configured && !connection?.connected) window.location.assign("/api/social/tiktok/connect"); }} className="mt-4 w-full rounded-xl border border-white/15 px-3 py-2 text-xs font-black disabled:cursor-not-allowed disabled:opacity-45">
                        {connection?.connected ? "Konto verbunden" : connection?.configured ? `${label} anmelden` : "Entwickler-App einrichten"}
                      </button>
                    </article>
                  );
                })}
              </div>
              <p className="mt-4 text-xs leading-5 text-white/45">
                Profilname und Profilbild können die offiziellen Posting-Schnittstellen nicht zuverlässig ändern. Beiträge, Bildunterschriften und Videos lassen sich nach Freigabe automatisieren.
              </p>
            </section>
            <section className="rounded-[1.8rem] border border-[#f8e9ae]/25 bg-white/[.06] p-5 sm:p-7">
              <p className="text-xs font-black uppercase tracking-[.18em] text-[#f8e9ae]">
                Marketing-Werkstatt
              </p>
              <h2 className="mt-2 text-3xl font-black">
                Ein Knopf, eine komplette Tageskampagne
              </h2>
              <p className="mt-3 max-w-3xl leading-7 text-white/70">
                Lumi recherchiert auf Wunsch selbst das Thema und erstellt E-Mail,
                Instagram- und TikTok-Inhalte. Du prüfst kurz und veröffentlichst direkt.
              </p>
              <form
                onSubmit={generateCampaign}
                className="mt-6 grid gap-3 md:grid-cols-[1fr_auto]"
              >
                <label className="sr-only" htmlFor="campaign-topic">
                  Kampagnenthema
                </label>
                <input
                  id="campaign-topic"
                  value={campaignTopic}
                  onChange={(event) => setCampaignTopic(event.target.value)}
                  maxLength={100}
                  placeholder="Optional – leer lassen, dann recherchiert Lumi selbst"
                  className="min-w-0 rounded-2xl border border-white/15 bg-[#0a0b28] px-4 py-3 text-white outline-none placeholder:text-white/35 focus:border-[#f8e9ae]"
                />
                <button
                  disabled={campaignWorking}
                  className="rounded-2xl bg-[#f8e9ae] px-5 py-3 font-black text-[#0d0e29] disabled:opacity-50"
                >
                  {campaignWorking ? "KI arbeitet …" : "KI-Kampagne starten"}
                </button>
              </form>
              <p className="mt-4 text-sm text-white/55">
                {campaigns?.eligibleRecipients || 0} verifizierte Kontakte haben
                Marketing erlaubt. Jede Kampagne entsteht individuell in allen neun Sprachen. {campaigns?.note}
              </p>
            </section>
            <section className="rounded-[1.8rem] border border-white/10 bg-white/[.05] p-5 sm:p-6">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div><h2 className="text-2xl font-black">Kunden & Zustellung</h2><p className="mt-1 text-sm text-white/55">Du siehst genau, wer erreichbar ist und warum eine Nachricht gesendet werden darf.</p></div>
                <span className="rounded-full bg-white/10 px-3 py-2 text-xs font-black">{campaigns?.audience?.length || 0} Kunden insgesamt</span>
              </div>
              <div className="mt-5 overflow-x-auto">
                <table className="min-w-[760px] w-full text-left text-sm" aria-label="Kampagnen-Kunden">
                  <thead className="border-b border-white/10 text-xs uppercase tracking-wider text-[#f8e9ae]/75"><tr><th className="p-3">Kunde</th><th className="p-3">Sprache</th><th className="p-3">Geschichten</th><th className="p-3">Status</th><th className="p-3">Zuletzt aktiv</th></tr></thead>
                  <tbody>{campaigns?.audience?.map((customer) => <tr key={customer.id} className="border-b border-white/5"><td className="p-3"><p className="font-bold">{customer.name}</p><p className="text-xs text-white/45">{customer.email}</p></td><td className="p-3 font-bold">{customer.language.toUpperCase()}</td><td className="p-3">{customer.storyCount}</td><td className="p-3"><span className={`rounded-full px-2.5 py-1 text-xs font-bold ${customer.allowed && customer.verified ? "bg-emerald-300/15 text-emerald-100" : "bg-white/10 text-white/50"}`}>{customer.allowed && customer.verified ? "Versand erlaubt" : !customer.verified ? "E-Mail unbestätigt" : "Keine Einwilligung"}</span></td><td className="p-3 text-white/55">{formatDate(customer.lastActiveAt)}</td></tr>)}</tbody>
                </table>
              </div>
            </section>
            <section className="grid gap-4 lg:grid-cols-2">
              {campaigns?.campaigns?.length ? (
                campaigns.campaigns
                  .filter((campaign) => campaign.status !== "archived")
                  .map((campaign) => (
                    <article
                      key={campaign.id}
                      className="rounded-[1.6rem] border border-white/10 bg-black/15 p-5"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-xs font-black uppercase tracking-[.16em] text-[#f8e9ae]">
                          {campaign.channel === "email"
                            ? "E-Mail"
                            : campaign.channel === "instagram"
                              ? "Instagram"
                              : "TikTok"}
                        </p>
                        <span className="text-xs text-white/40">
                          {campaign.language.toUpperCase()}
                        </span>
                      </div>
                      <h3 className="mt-3 text-xl font-black">
                        {campaign.title}
                      </h3>
                      <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-white/70">
                        {campaign.message}
                      </p>
                      <button
                        type="button"
                        onClick={() => void copyCampaign(campaign)}
                        className="mt-5 rounded-xl border border-white/20 px-4 py-2 text-sm font-black"
                      >
                        Text kopieren
                      </button>
                      {campaign.channel === "email" ? (
                        <button
                          type="button"
                          disabled={campaignWorking || campaign.status === "sent" || !campaigns?.connections.email.connected}
                          onClick={() => void sendCampaign(campaign)}
                          className="ml-2 mt-5 rounded-xl bg-[#f8e9ae] px-4 py-2 text-sm font-black text-[#0d0e29] disabled:cursor-not-allowed disabled:opacity-45"
                        >
                          {campaign.status === "sent" ? `Versendet an ${campaign.recipientCount || 0}` : `An ${campaigns?.eligibleRecipients || 0} Kontakte senden`}
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled={campaignWorking || campaign.status === "sent" || !campaigns?.connections[campaign.channel].connected}
                          onClick={() => void publishSocialCampaign(campaign)}
                          className="ml-2 mt-5 rounded-xl bg-[#f8e9ae] px-4 py-2 text-sm font-black text-[#0d0e29] disabled:cursor-not-allowed disabled:opacity-45"
                        >
                          {campaign.status === "sent" ? "Veröffentlicht" : `Jetzt auf ${campaign.channel === "instagram" ? "Instagram" : "TikTok"} posten`}
                        </button>
                      )}
                    </article>
                  ))
              ) : (
                <p className="rounded-2xl bg-black/15 p-5 text-white/55">
                  Noch keine Kampagne. Gib oben ein Thema ein.
                </p>
              )}
            </section>
          </section>
        ) : null}
      </div>
    </main>
  );
}
